/**
 * Internet access lives on the edit screen with the rest of the specialist's
 * configuration rather than spending a tab beside the surfaces opened daily.
 */
export const AGENT_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "skills", label: "Skills" },
  { id: "connectors", label: "Connectors" },
  { id: "permissions", label: "Tools" },
  { id: "browser", label: "Browser" },
  { id: "memory", label: "Memory" },
  { id: "email", label: "Email" },
  { id: "schedules", label: "Scheduled jobs" },
  { id: "access", label: "Access" },
] as const;

export type AgentDetailTab = (typeof AGENT_DETAIL_TABS)[number]["id"];

export function normalizeAgentDetailTab(value: string | undefined): AgentDetailTab {
  return AGENT_DETAIL_TABS.some((tab) => tab.id === value) ? (value as AgentDetailTab) : "overview";
}

export function agentDetailPath(agentId: string, tab: AgentDetailTab = "overview"): string {
  const root = `/specialists/${encodeURIComponent(agentId)}`;
  return tab === "overview" ? root : `${root}/${tab}`;
}

export function scheduledTaskPath(agentId: string, scheduleId: string): string {
  return `${agentDetailPath(agentId, "schedules")}?schedule=${encodeURIComponent(scheduleId)}`;
}

/** Administrator recovery route for a specialist whose detail read is forbidden. */
export function agentAccessRecoveryPath(agentId: string): string {
  return `${agentDetailPath(agentId)}/access`;
}

/**
 * Permissions-tab group slug used as the trailing path segment for the
 * master-detail sub-route (`/specialists/<id>/permissions/<group>`). The slug
 * is the stable deep-link identifier for a permission group; an unrecognized
 * or empty value resolves to the group-list landing.
 */
export function normalizePermissionsGroupSlug(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Builds a deep link to a single permission group's detail view. Connector
 * profiles carry both a connector type and an instance name, so their slug is
 * composite; the fixed groups (`builtins`, `gateway`, `memory`) use a literal.
 */
export function permissionGroupPath(agentId: string, groupSlug: string): string {
  return `${agentDetailPath(agentId, "permissions")}/${encodeURIComponent(groupSlug)}`;
}

/**
 * Conversations is a sub-route off the agent page, not a top-level tab — the
 * Overview tab's "Recent conversations" widget links here for the full,
 * paginated list of an agent's conversations.
 */
export function agentConversationsPath(agentId: string): string {
  return `/specialists/${encodeURIComponent(agentId)}/conversations`;
}

/**
 * A specialist conversation URL that `resolvePortalRedirect` lands on the
 * equivalent Workspace conversation. Used by the dashboard and agent-overview
 * conversation rows.
 */
export function conversationDetailPath(agentId: string, sessionId: string): string {
  return `${agentConversationsPath(agentId)}/${encodeURIComponent(sessionId)}`;
}

/**
 * A specialist chat URL that `resolvePortalRedirect` lands on a Workspace
 * conversation with that specialist. Used by the specialist Chat button and the
 * live-activity feed; a client-minted UUID identifies a stable conversation.
 */
export function agentChatPath(agentId: string, sessionId?: string): string {
  const base = `/specialists/${encodeURIComponent(agentId)}/chat`;
  return sessionId ? `${base}/${encodeURIComponent(sessionId)}` : base;
}
